<template>
  <div class="principal-workspace brown lighten-5" :class="{ 'is-inline': inlineMode }" ref="workspaceShell">
    <a
      v-if="!inlineMode"
      href="#"
      class="workspace-side-cancel workspace-side-cancel-left"
      :class="{ visible: showSideCancel }"
      title="Cancel pop-up"
      @click.prevent="handleClose"
    >
      <i class="material-icons">cancel</i>
    </a>
    <a
      v-if="!inlineMode"
      href="#"
      class="workspace-side-cancel workspace-side-cancel-right"
      :class="{ visible: showSideCancel }"
      title="Cancel pop-up"
      @click.prevent="handleClose"
    >
      <i class="material-icons">cancel</i>
    </a>

    <div class="container workspace-container">
      <div class="workspace-top" ref="workspaceTop" v-if="!inlineMode">
        <a href="#" class="workspace-cancel-card" title="Cancel pop-up" @click.prevent="handleClose">
          <i class="material-icons">close</i>
          <span>Cancel pop-up</span>
        </a>
      </div>

      <div class="workspace-identity white" @click.stop>
        <button
          class="btn-flat workspace-inline-close"
          title="Close principal detail"
          @click.prevent="handleClose"
          v-if="inlineMode"
        >
          <i class="material-icons left">close</i>Close
        </button>

        <div class="row valign-wrapper">
          <div class="col s3 m2">
            <img alt="" :src="currentPrincipal.profile_img" class="circle responsive-img" v-if="currentPrincipal.profile_img" />
            <img alt=""
              :src="`https://ui-avatars.com/api/?name=${currentPrincipal.name}&color=FFFFFF&size=128`"
              class="circle responsive-img"
              v-else
            />
          </div>
          <div class="col s9 m7">
            <p class="grey-text text-darken-1 m-0">{{ currentPrincipal.enrollee_number || "No enrollee number" }}</p>
            <h4 class="m-0">{{ currentPrincipal.name }}</h4>
            <p>
              <span class="chip teal lighten-5">{{ principalType }}</span>
              <span class="chip workspace-access-chip" :class="accessStatusClass">
                <i class="material-icons">{{ accessStatusIcon }}</i>
                {{ accessStatusLabel }}
              </span>
              <span class="chip orange lighten-5" v-if="currentPrincipal.provider">{{ currentPrincipal.provider.name }}</span>
            </p>
            <p class="workspace-hold-note" v-if="isOnHoldPrincipal">
              <i class="material-icons">pause_circle</i>
              <span>No active invoice. Treat cover access as inactive until billing is restored.</span>
            </p>
          </div>
          <div class="col s12 m3 hide-on-small-only right-align">
            <label class="active teal-text">Plan</label>
            <h6>{{ currentPrincipal.plan?.name || "No plan" }}</h6>
            <small class="grey-text" v-if="isFamilyPrincipal">
              {{ currentPrincipal.spouse ? "Spouse" : "No spouse" }} |
              {{ dependantCount }} / {{ dependantCapacity }} dependant(s)
            </small>
          </div>
        </div>
      </div>

      <div class="workspace-tabs white" @click.stop>
        <a
          href="#"
          class="workspace-tab"
          :class="{ active: activePanel == 'details' }"
          @click.prevent="setPanel('details')"
        >
          <i class="material-icons">badge</i><span>Principal</span>
        </a>
        <a
          href="#"
          class="workspace-tab"
          :class="{ active: activePanel == 'spouse', disabled: !isFamilyPrincipal }"
          @click.prevent="setPanel('spouse')"
        >
          <i class="material-icons">favorite</i><span>Spouse</span>
        </a>
        <a
          href="#"
          class="workspace-tab"
          :class="{ active: activePanel == 'dependants', disabled: !isFamilyPrincipal }"
          @click.prevent="setPanel('dependants')"
        >
          <i class="material-icons">groups</i><span>Dependants</span>
        </a>
        <a
          href="#"
          class="workspace-tab"
          :class="{ active: activePanel == 'utilization' }"
          @click.prevent="setPanel('utilization')"
        >
          <i class="material-icons">insights</i><span>Utilization</span>
        </a>
        <a
          href="#"
          class="workspace-tab danger"
          :class="{ active: activePanel == 'status' }"
          @click.prevent="setPanel('status')"
        >
          <i class="material-icons">admin_panel_settings</i>
          <span>Critical Changes</span>
        </a>
      </div>

      <div class="workspace-panel white" @click.stop>
        <template v-if="activePanel == 'details'">
          <template v-if="principalMode == 'edit'">
            <div class="edit-top-actions">
              <a href="#" class="inline-back-icon" title="Back" @click.prevent="closeInlineEdit">
                <i class="material-icons">arrow_back</i>
              </a>
            </div>
            <EnrolleeEditDiv :enrollee="currentPrincipal" :policy="currentPolicy" :server-date="serverDate" @reset="handleReset" />
          </template>
          <template v-else-if="principalMode == 'provider'">
            <div class="edit-top-actions">
              <a href="#" class="inline-back-icon" title="Back" @click.prevent="closeInlineEdit">
                <i class="material-icons">arrow_back</i>
              </a>
            </div>
            <EnrolleeProviderDiv :user="currentPrincipal" @reset="handleReset" />
          </template>

          <div class="detail-section" v-else>
            <div class="detail-heading">
              <h6>Principal Information</h6>
              <span class="principal-action-bar">
                <a href="#" class="btn-small waves-effect waves-light edit-action account-action" @click.prevent="openPrincipalEdit">
                  <i class="material-icons left">edit</i>Account
                </a>
                <a href="#" class="btn-small waves-effect waves-light edit-action provider-action" @click.prevent="openProviderEdit">
                  <i class="material-icons left">sync_alt</i>Provider
                </a>
                <small>{{ currentPrincipal.enrollee_number || "No enrollee number" }}</small>
              </span>
            </div>

            <div class="detail-row">
              <span>Client</span>
              <strong>{{ displayClientName }}</strong>
            </div>
            <div class="detail-row">
              <span>System ID</span>
              <strong>{{ formattedSystemId }}</strong>
            </div>
            <div class="detail-row">
              <span>Enrollee ID</span>
              <strong>{{ currentPrincipal.enrollee_number || "-" }}</strong>
            </div>
            <div class="detail-row">
              <span>Full Name</span>
              <strong>{{ currentPrincipal.name || "-" }}</strong>
            </div>
            <div class="detail-row">
              <span>Sex</span>
              <strong>{{ sexLabel(currentPrincipal.sex) }}</strong>
            </div>
            <div class="detail-row">
              <span>Date of Birth</span>
              <strong>{{ formatDate(currentPrincipal.dob) || "-" }}</strong>
            </div>
            <div class="detail-row">
              <span>Category</span>
              <strong class="principal-type-value">
                <i class="material-icons">{{ isFamilyPrincipal ? "groups" : "person" }}</i>
                {{ principalType }}
              </strong>
            </div>
            <div class="detail-row plan-highlight-row">
              <span>Plan</span>
              <strong>{{ currentPrincipal.plan?.name || "-" }}</strong>
            </div>
            <div class="detail-row">
              <span>Provider</span>
              <strong>{{ currentPrincipal.provider?.name || "-" }}</strong>
            </div>
            <div
              class="detail-row access-highlight-row"
              :class="{ 'is-inactive': isOnHoldPrincipal || currentPrincipal.status == 'd' }"
            >
              <span>Cover Access</span>
              <strong>
                <i class="material-icons">{{ accessStatusIcon }}</i>
                {{ accessStatusDetail }}
              </strong>
            </div>
            <div class="detail-row">
              <span>Dependants</span>
              <strong>{{ isFamilyPrincipal ? dependantCapacityLabel : "N/A" }}</strong>
            </div>
            <div class="detail-row">
              <span>Email</span>
              <strong>{{ principalEmail }}</strong>
            </div>
            <div class="detail-row">
              <span>Phone</span>
              <strong>{{ currentPrincipal.phone || "-" }}</strong>
            </div>
            <div class="detail-row detail-row-wide">
              <span>Address</span>
              <strong>{{ currentPrincipal.address || "-" }}</strong>
            </div>
            <div class="detail-row">
              <span>Registered</span>
              <strong>{{ formatDate(currentPrincipal.created_at) || "-" }}</strong>
            </div>
            <div class="detail-row">
              <span>Last Updated</span>
              <strong>{{ formatDate(currentPrincipal.updated_at) || "-" }}</strong>
            </div>
          </div>
        </template>

        <template v-if="activePanel == 'spouse'">
          <div class="center section" v-if="!isFamilyPrincipal">
            <h6>Spouse management is available only for family plans.</h6>
          </div>
          <template v-else-if="spouseMode == 'add'">
            <div class="edit-top-actions">
              <a href="#" class="inline-back-icon" title="Back" @click.prevent="closeInlineEdit">
                <i class="material-icons">arrow_back</i>
              </a>
            </div>
            <AddSpouseDiv
              :enrollee="currentPrincipal"
              :policy="currentPolicy"
              :server-date="serverDate"
              @reset="handleReset"
            />
          </template>
          <template v-else-if="currentPrincipal.spouse && spouseMode == 'edit'">
            <div class="edit-top-actions">
              <a href="#" class="inline-back-icon" title="Back" @click.prevent="closeInlineEdit">
                <i class="material-icons">arrow_back</i>
              </a>
            </div>
            <SpouseEditDiv
              :spouse="currentPrincipal.spouse"
              :principal="currentPrincipal"
              :policy="currentPolicy"
              :server-date="serverDate"
              @reset="handleReset"
            />
          </template>
          <template v-else-if="currentPrincipal.spouse && spouseMode == 'provider'">
            <div class="edit-top-actions">
              <a href="#" class="inline-back-icon" title="Back" @click.prevent="closeInlineEdit">
                <i class="material-icons">arrow_back</i>
              </a>
            </div>
            <SpouseProviderDiv
              :user="memberWithPrincipalPlan(currentPrincipal.spouse)"
              :principal="currentPrincipal"
              @reset="handleReset"
            />
          </template>
          <div class="detail-section" v-else-if="currentPrincipal.spouse">
            <div class="detail-heading">
              <h6>Spouse Information</h6>
              <span class="principal-action-bar">
                <a href="#" class="btn-small waves-effect waves-light edit-action account-action" @click.prevent="spouseMode = 'edit'">
                  <i class="material-icons left">edit</i>Account
                </a>
                <a
                  href="#"
                  class="btn-small waves-effect waves-light edit-action provider-action"
                  @click.prevent="openSpouseProviderEdit"
                >
                  <i class="material-icons left">sync_alt</i>Provider
                </a>
                <small>{{ currentPrincipal.spouse.enrollee_number || "No enrollee number" }}</small>
              </span>
            </div>
            <div class="detail-row">
              <span>System ID</span>
              <strong>{{ formattedMemberSystemId(currentPrincipal.spouse) }}</strong>
            </div>
            <div class="detail-row">
              <span>Enrollee ID</span>
              <strong>{{ currentPrincipal.spouse.enrollee_number || "-" }}</strong>
            </div>
            <div class="detail-row">
              <span>Full Name</span>
              <strong>{{ currentPrincipal.spouse.name || "-" }}</strong>
            </div>
            <div class="detail-row">
              <span>Sex</span>
              <strong>{{ sexLabel(currentPrincipal.spouse.sex) }}</strong>
            </div>
            <div class="detail-row">
              <span>Date of Birth</span>
              <strong>{{ formatDate(currentPrincipal.spouse.dob) || "-" }}</strong>
            </div>
            <div class="detail-row">
              <span>Phone</span>
              <strong>{{ currentPrincipal.spouse.phone || "-" }}</strong>
            </div>
            <div class="detail-row">
              <span>Email</span>
              <strong>{{ memberEmail(currentPrincipal.spouse) }}</strong>
            </div>
            <div class="detail-row detail-row-wide">
              <span>Address</span>
              <strong>{{ currentPrincipal.spouse.address || "-" }}</strong>
            </div>
            <div class="detail-row">
              <span>Provider</span>
              <strong>{{ currentPrincipal.spouse.provider?.name || currentPrincipal.provider?.name || "-" }}</strong>
            </div>
            <div class="detail-row">
              <span>Status</span>
              <strong>{{ currentPrincipal.spouse.status == "d" ? "Inactive" : "Active" }}</strong>
            </div>
            <div class="detail-row">
              <span>Registered</span>
              <strong>{{ formatDate(currentPrincipal.spouse.created_at) || "-" }}</strong>
            </div>
            <div class="detail-row">
              <span>Last Updated</span>
              <strong>{{ formatDate(currentPrincipal.spouse.updated_at) || "-" }}</strong>
            </div>
          </div>
          <div class="detail-section" v-else>
            <div class="detail-heading">
              <h6>Spouse Information</h6>
              <span>
                <a
                  href="#"
                  class="btn-small waves-effect waves-light edit-action"
                  @click.prevent="spouseMode = 'add'"
                  v-if="canAddSpouse"
                >
                  <i class="material-icons left">person_add</i>Add Spouse
                </a>
                <small>No spouse record</small>
              </span>
            </div>
            <div class="detail-row">
              <span>Status</span>
              <strong>No spouse has been added for this principal.</strong>
            </div>
          </div>
        </template>

        <template v-if="activePanel == 'dependants'">
          <div class="center section" v-if="!isFamilyPrincipal">
            <h6>Dependant management is available only for family plans.</h6>
          </div>
          <template v-else>
            <template v-if="canAddDependant && dependantMode == 'add'">
              <div class="edit-top-actions">
                <a href="#" class="inline-back-icon" title="Back" @click.prevent="closeInlineEdit">
                  <i class="material-icons">arrow_back</i>
                </a>
              </div>
              <AddDependantDiv
                :enrollee="currentPrincipal"
                :policy="currentPolicy"
                :server-date="serverDate"
                @reset="handleReset"
              />
            </template>
            <template v-else-if="selectedDependant && dependantMode == 'edit'">
              <div class="edit-top-actions">
                <a href="#" class="inline-back-icon" title="Back" @click.prevent="closeInlineEdit">
                  <i class="material-icons">arrow_back</i>
                </a>
              </div>
              <DependantEditDiv
                :dependant="selectedDependant"
                :principal="currentPrincipal"
                :policy="currentPolicy"
                :server-date="serverDate"
                @reset="handleReset"
              />
            </template>
            <template v-else-if="selectedDependant && dependantMode == 'provider'">
              <div class="edit-top-actions">
                <a href="#" class="inline-back-icon" title="Back" @click.prevent="closeInlineEdit">
                  <i class="material-icons">arrow_back</i>
                </a>
              </div>
              <DependantProviderDiv
                :user="memberWithPrincipalPlan(selectedDependant)"
                :principal="currentPrincipal"
                @reset="handleReset"
              />
            </template>
            <div
              class="right-align dependant-actions"
              v-if="canAddDependant && currentPrincipal.dependants?.length && dependantMode == 'list'"
            >
              <a href="#" class="btn waves-effect waves-light" @click.prevent="dependantMode = 'add'">
                <i class="material-icons left">person_add</i>Add Dependant
              </a>
              <small class="grey-text">{{ dependantSlotsRemaining }} slot(s) remaining</small>
            </div>
            <div v-if="currentPrincipal.dependants?.length && dependantMode == 'list'">
              <div
                class="detail-section dependant-detail-section"
                v-for="(dependant, index) in currentPrincipal.dependants"
                :key="dependant.id"
              >
                <div class="detail-heading">
                  <h6>Dependant {{ index + 1 }}</h6>
                  <span class="principal-action-bar">
                    <a href="#" class="btn-small waves-effect waves-light edit-action account-action" @click.prevent="handleEditDependant(dependant)">
                      <i class="material-icons left">edit</i>Account
                    </a>
                    <a
                      href="#"
                      class="btn-small waves-effect waves-light edit-action provider-action"
                      @click.prevent="handleEditDependantProvider(dependant)"
                    >
                      <i class="material-icons left">sync_alt</i>Provider
                    </a>
                    <small>{{ dependant.enrollee_number || "No enrollee number" }}</small>
                  </span>
                </div>
                <div class="detail-row">
                  <span>System ID</span>
                  <strong>{{ formattedMemberSystemId(dependant) }}</strong>
                </div>
                <div class="detail-row">
                  <span>Enrollee ID</span>
                  <strong>{{ dependant.enrollee_number || "-" }}</strong>
                </div>
                <div class="detail-row">
                  <span>Full Name</span>
                  <strong>{{ dependant.name || "-" }}</strong>
                </div>
                <div class="detail-row">
                  <span>Sex</span>
                  <strong>{{ sexLabel(dependant.sex) }}</strong>
                </div>
                <div class="detail-row">
                  <span>Date of Birth</span>
                  <strong>{{ formatDate(dependant.dob) || "-" }}</strong>
                </div>
                <div class="detail-row">
                  <span>Phone</span>
                  <strong>{{ dependant.phone || "-" }}</strong>
                </div>
                <div class="detail-row">
                  <span>Email</span>
                  <strong>{{ memberEmail(dependant) }}</strong>
                </div>
                <div class="detail-row detail-row-wide">
                  <span>Address</span>
                  <strong>{{ dependant.address || "-" }}</strong>
                </div>
                <div class="detail-row">
                  <span>Provider</span>
                  <strong>{{ dependant.provider?.name || currentPrincipal.provider?.name || "-" }}</strong>
                </div>
                <div class="detail-row">
                  <span>Status</span>
                  <strong>{{ dependant.status == "d" ? "Inactive" : "Active" }}</strong>
                </div>
                <div class="detail-row">
                  <span>Registered</span>
                  <strong>{{ formatDate(dependant.created_at) || "-" }}</strong>
                </div>
                <div class="detail-row">
                  <span>Last Updated</span>
                  <strong>{{ formatDate(dependant.updated_at) || "-" }}</strong>
                </div>
              </div>
            </div>
            <div class="detail-section" v-if="!currentPrincipal.dependants?.length && dependantMode != 'add'">
              <div class="detail-heading">
                <h6>Dependant Information</h6>
                <span>
                  <a
                    href="#"
                    class="btn-small waves-effect waves-light edit-action"
                    @click.prevent="dependantMode = 'add'"
                    v-if="canAddDependant"
                  >
                    <i class="material-icons left">person_add</i>Add Dependant
                  </a>
                  <small>No dependant record</small>
                </span>
              </div>
              <div class="detail-row">
                <span>Status</span>
                <strong>No dependant has been added for this principal.</strong>
              </div>
            </div>
          </template>
        </template>

        <template v-if="activePanel == 'plan'">
          <template v-if="planMode == 'edit'">
            <div class="edit-top-actions">
              <a href="#" class="inline-back-icon" title="Back" @click.prevent="closeInlineEdit">
                <i class="material-icons">arrow_back</i>
              </a>
            </div>
            <EnrolleePlanDiv :enrollee="currentPrincipal" @reset="handleReset" />
          </template>
          <div class="detail-section" v-else>
            <div class="detail-heading">
              <h6>Plan Information</h6>
              <span>
                <a href="#" class="btn-small waves-effect waves-light edit-action" @click.prevent="planMode = 'edit'">
                  <i class="material-icons left">edit</i>Edit
                </a>
                <small>{{ currentPrincipal.plan?.name || "No plan" }}</small>
              </span>
            </div>
            <div class="detail-row">
              <span>Plan</span>
              <strong>{{ currentPrincipal.plan?.name || "-" }}</strong>
            </div>
            <div class="detail-row">
              <span>Plan Type</span>
              <strong>{{ principalType }}</strong>
            </div>
            <div class="detail-row">
              <span>Provider</span>
              <strong>{{ currentPrincipal.provider?.name || "-" }}</strong>
            </div>
            <div class="detail-row">
              <span>Dependants</span>
              <strong>{{ isFamilyPrincipal ? dependantCapacityLabel : "N/A" }}</strong>
            </div>
            <div class="detail-row">
              <span>Policy Year</span>
              <strong>{{ currentPolicyLabel }}</strong>
            </div>
          </div>
        </template>

        <template v-if="activePanel == 'status'">
          <div class="critical-workbench">
            <div class="critical-menu">
              <a
                href="#"
                class="critical-menu-item"
                :class="{ active: statusMode == 'home' }"
                @click.prevent="openCriticalAction('home')"
              >
                <i class="material-icons">home</i>
                <span>
                  <strong>Home</strong>
                  <small>Impact summary</small>
                </span>
              </a>
              <a
                href="#"
                class="critical-menu-item"
                :class="{ active: statusMode == 'plan' }"
                @click.prevent="openCriticalAction('plan')"
              >
                <i class="material-icons">assignment</i>
                <span>
                  <strong>Plan Change</strong>
                  <small>{{ currentPrincipal.plan?.name || "No plan" }}</small>
                </span>
              </a>
              <a
                href="#"
                class="critical-menu-item"
                :class="{ active: statusMode == 'id' }"
                @click.prevent="openCriticalAction('id')"
              >
                <i class="material-icons">badge</i>
                <span>
                  <strong>Update ID</strong>
                  <small>Principal and family IDs</small>
                </span>
              </a>
              <a
                href="#"
                class="critical-menu-item danger"
                :class="{ active: statusMode == 'edit' }"
                @click.prevent="openCriticalAction('edit')"
              >
                <i class="material-icons">{{ currentPrincipal.status == "d" ? "restore" : "person_off" }}</i>
                <span>
                  <strong>{{ currentPrincipal.status == "d" ? "Restore" : "De-activate" }}</strong>
                  <small>{{ statusLabel }}</small>
                </span>
              </a>
            </div>

            <div class="critical-main">
              <template v-if="statusMode == 'plan'">
                <EnrolleePlanDiv :enrollee="currentPrincipal" @reset="handleReset" />
              </template>
              <template v-else-if="statusMode == 'id'">
                <div class="id-update-panel">
                  <div class="critical-home-heading">
                    <i class="material-icons">badge</i>
                    <span>
                      <h6>Update Enrollee IDs</h6>
                      <small>Applies to the selected member's active profile</small>
                    </span>
                  </div>
                  <p class="id-update-note">
                    Past hospital visits and claims keep the ID they had at the time. This update affects the member's current ID going forward.
                  </p>
                  <div class="id-update-list">
                    <div class="id-update-row" v-for="member in idMembers" :key="`${member.type}-${member.id}`">
                      <div>
                        <strong>{{ member.name }}</strong>
                        <small>{{ member.label }} | Current ID: {{ member.enrollee_number || "-" }}</small>
                      </div>
                      <div class="id-update-controls">
                        <input
                          type="text"
                          v-model="idDrafts[member.key]"
                          :placeholder="member.enrollee_number || 'Enter enrollee ID'"
                        />
                        <button
                          class="btn-small waves-effect waves-light"
                          :class="{ disabled: !canSaveId(member) || savingIdKey == member.key }"
                          @click.prevent="handleUpdateId(member)"
                        >
                          {{ savingIdKey == member.key ? "Saving" : "Save" }}
                        </button>
                      </div>
                    </div>
                  </div>
                  <p class="red-text text-darken-2" v-if="idError">{{ idError }}</p>
                </div>
              </template>
              <template v-else-if="statusMode == 'edit'">
                <EnrolleeStatusDiv :cat="cat" :enrollee="currentPrincipal" @reset="handleReset" />
              </template>
              <div class="critical-home" v-else>
                <div class="critical-home-heading">
                  <i class="material-icons">report_problem</i>
                  <span>
                    <h6>Critical Changes</h6>
                    <small>Authorized administration only</small>
                  </span>
                </div>
                <p>
                  Actions in this area can change billing, invoicing, cover rules, enrollee lists,
                  encounter history, and utilization reports. Only proceed when the request has been
                  reviewed and the downstream effect is understood.
                </p>
                <div class="critical-summary">
                  <div>
                    <span>Current plan</span>
                    <strong>{{ currentPrincipal.plan?.name || "-" }}</strong>
                  </div>
                  <div>
                    <span>Policy Year</span>
                    <strong>{{ currentPolicyLabel }}</strong>
                  </div>
                  <div>
                    <span>Status</span>
                    <strong>{{ statusLabel }}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <template v-if="activePanel == 'utilization'">
          <PrincipalInsightDiv
            :clientId="currentPrincipal.client_id"
            :principalId="currentPrincipal.id"
            :policyId="policyId"
            @reset="setPanel('details')"
            v-if="currentPrincipal.client_id && policyId"
          />
          <div class="center section" v-else>
            <h6 class="empty-utilization">
              <i class="material-icons">insights</i>
              <span>Utilization details are not available for this principal yet.</span>
            </h6>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import putItem from "@/composables/putItem";
import EnrolleeStatusDiv from "@/components/client/onboard/EnrolleeStatusEditOnboardComponent.vue";
import EnrolleeProviderDiv from "@/components/client/onboard/EnrolleeProviderOnboardComponent.vue";
import SpouseProviderDiv from "@/components/client/onboard/EnrolleeProviderSpouseOnboardComponent.vue";
import DependantProviderDiv from "@/components/client/onboard/EnrolleeProviderDependantOnboardComponent.vue";
import EnrolleePlanDiv from "@/components/client/onboard/EnrolleePlanEditOnboardComponent.vue";
import EnrolleeEditDiv from "@/components/client/onboard/EnrolleeEditOnboardComponent.vue";
import AddSpouseDiv from "@/components/client/onboard/EnrolleeAddSpouseOnboardComponent.vue";
import AddDependantDiv from "@/components/client/onboard/EnrolleeAddDependantOnboardComponent.vue";
import SpouseEditDiv from "@/components/client/onboard/EnrolleeEditSpouseOnboardComponent.vue";
import DependantEditDiv from "@/components/client/onboard/EnrolleeEditDependantOnboardComponent.vue";
import PrincipalInsightDiv from "@/components/insight/PrincipalDetailInsightComponent.vue";

export default {
  name: "PrincipalWorkspaceClientComponent",
  components: {
    EnrolleeStatusDiv,
    EnrolleeProviderDiv,
    SpouseProviderDiv,
    DependantProviderDiv,
    EnrolleePlanDiv,
    EnrolleeEditDiv,
    AddSpouseDiv,
    AddDependantDiv,
    SpouseEditDiv,
    DependantEditDiv,
    PrincipalInsightDiv,
  },
  props: ["principal", "authLevel", "cat", "clientName", "displayType", "startPanel", "policy", "serverDate", "inline"],
  emits: ["close", "reset"],
  setup(props, context) {
    const currentPrincipal = ref({ ...props.principal });
    const activePanel = ref(props.startPanel || "details");
    const principalMode = ref("view");
    const spouseMode = ref("view");
    const dependantMode = ref("list");
    const planMode = ref("view");
    const statusMode = ref("home");
    const selectedDependant = ref(null);
    const showSideCancel = ref(false);
    const workspaceShell = ref(null);
    const workspaceTop = ref(null);
    const idDrafts = ref({});
    const idError = ref(null);
    const savingIdKey = ref(null);
    const inlineMode = computed(() => props.inline === true || props.inline === "" || props.inline === "true");

    watch(
      () => props.principal,
      (val) => {
        currentPrincipal.value = { ...val };
      }
    );

    const isFamilyPrincipal = computed(() => currentPrincipal.value?.type == "f");
    const isOnHoldPrincipal = computed(() => props.displayType == "h");
    const displayClientName = computed(() => props.clientName || currentPrincipal.value?.client?.name || "-");
    const dependantCount = computed(() => currentPrincipal.value?.dependants?.length || 0);
    const principalType = computed(() => (isFamilyPrincipal.value ? "Family" : "Individual"));
    const principalEmail = computed(() => currentPrincipal.value?.email || currentPrincipal.value?.user?.email || "-");
    const statusLabel = computed(() => {
      if (currentPrincipal.value?.status == "d") return "Inactive";
      if (currentPrincipal.value?.status == "p") return "Pending";
      return "Active";
    });
    const accessStatusLabel = computed(() => {
      if (currentPrincipal.value?.status == "d" || isOnHoldPrincipal.value) return "Inactive";
      if (currentPrincipal.value?.status == "p") return "Pending";
      return "Active";
    });
    const accessStatusDetail = computed(() => {
      if (isOnHoldPrincipal.value) return "Inactive - no active invoice";
      if (currentPrincipal.value?.status == "d") return "Inactive";
      if (currentPrincipal.value?.status == "p") return "Pending activation";
      return "Active";
    });
    const accessStatusClass = computed(() => {
      if (currentPrincipal.value?.status == "d" || isOnHoldPrincipal.value) return "is-inactive";
      if (currentPrincipal.value?.status == "p") return "is-pending";
      return "is-active";
    });
    const accessStatusIcon = computed(() => {
      if (currentPrincipal.value?.status == "d" || isOnHoldPrincipal.value) return "block";
      if (currentPrincipal.value?.status == "p") return "schedule";
      return "verified_user";
    });
    const canAddSpouse = computed(() => isFamilyPrincipal.value && !currentPrincipal.value?.spouse && props.authLevel >= 1);
    const currentPolicy = computed(() => {
      return (
        props.policy ||
        currentPrincipal.value?.policy ||
        currentPrincipal.value?.client?.policy ||
        currentPrincipal.value?.plan?.policy ||
        null
      );
    });
    const dependantCapacity = computed(() => {
      const familySize = Number(currentPolicy.value?.family_size);

      if (Number.isInteger(familySize) && familySize > 0) {
        return Math.max(familySize - 2, 0);
      }

      return 4;
    });
    const dependantSlotsRemaining = computed(() => {
      return Math.max(dependantCapacity.value - dependantCount.value, 0);
    });
    const dependantCapacityLabel = computed(() => {
      return `${dependantCount.value} / ${dependantCapacity.value}`;
    });
    const canAddDependant = computed(() => {
      return isFamilyPrincipal.value && dependantSlotsRemaining.value > 0 && props.authLevel >= 1;
    });
    const policyId = computed(() => {
      return currentPolicy.value?.id || currentPrincipal.value?.policy_id || currentPrincipal.value?.plan?.policy_id || null;
    });
    const currentPolicyLabel = computed(() => {
      const policy = currentPolicy.value;

      if (!policy) {
        return policyId.value ? `Policy #${policyId.value}` : "-";
      }

      const year = policy.year || policy.name || policy.description || null;
      const range = policyDateRange(policy);

      if (year && range) return `${year} | ${range}`;
      return year || range || (policy.id ? `Policy #${policy.id}` : "-");
    });
    const formattedSystemId = computed(() => {
      const principalId = currentPrincipal.value?.id;
      if (!principalId) return "-";

      const clientPrefix =
        currentPrincipal.value?.client?.client_number ||
        currentPrincipal.value?.client?.number ||
        currentPrincipal.value?.client?.code ||
        currentPrincipal.value?.client_id ||
        null;

      const paddedPrincipalId = String(principalId).padStart(4, "0");

      if (!clientPrefix) {
        return paddedPrincipalId;
      }

      return `${clientPrefix}/${paddedPrincipalId}`;
    });

    const idMembers = computed(() => {
      const members = [];

      if (currentPrincipal.value?.id) {
        members.push({
          id: currentPrincipal.value.id,
          key: `principal-${currentPrincipal.value.id}`,
          type: "principal",
          label: "Principal",
          name: currentPrincipal.value.name,
          enrollee_number: currentPrincipal.value.enrollee_number,
        });
      }

      if (currentPrincipal.value?.spouse?.id) {
        members.push({
          id: currentPrincipal.value.spouse.id,
          key: `spouse-${currentPrincipal.value.spouse.id}`,
          type: "spouse",
          label: "Spouse",
          name: currentPrincipal.value.spouse.name,
          enrollee_number: currentPrincipal.value.spouse.enrollee_number,
        });
      }

      (currentPrincipal.value?.dependants || []).forEach((dependant, index) => {
        members.push({
          id: dependant.id,
          key: `dependant-${dependant.id}`,
          type: "dependant",
          label: `Dependant ${index + 1}`,
          name: dependant.name,
          enrollee_number: dependant.enrollee_number,
        });
      });

      return members;
    });

    watch(
      idMembers,
      (members) => {
        const drafts = { ...idDrafts.value };
        members.forEach((member) => {
          if (!Object.prototype.hasOwnProperty.call(drafts, member.key)) {
            drafts[member.key] = member.enrollee_number || "";
          }
        });
        idDrafts.value = drafts;
      },
      { immediate: true }
    );

    const setPanel = (panel) => {
      if ((panel == "spouse" || panel == "dependants") && !isFamilyPrincipal.value) {
        return;
      }

      principalMode.value = "view";
      spouseMode.value = "view";
      dependantMode.value = "list";
      planMode.value = "view";
      statusMode.value = "home";
      selectedDependant.value = null;
      activePanel.value = panel;
    };

    const canSaveId = (member) => {
      const draft = (idDrafts.value[member.key] || "").trim();
      return Boolean(draft && draft !== (member.enrollee_number || ""));
    };

    const handleUpdateId = async (member) => {
      if (!canSaveId(member)) {
        return;
      }

      savingIdKey.value = member.key;
      idError.value = null;

      const newVal = {
        member_type: member.type,
        member_id: member.id,
        enrollee_number: idDrafts.value[member.key].trim(),
      };

      const { res, err } = await putItem(
        "operator/client/enrollee/id/update",
        currentPrincipal.value.id,
        newVal
      );

      if (res.value) {
        currentPrincipal.value = { ...currentPrincipal.value, ...res.value.data };
        idDrafts.value = {};
        context.emit("reset", currentPrincipal.value);
      } else {
        idError.value = err.value;
      }

      savingIdKey.value = null;
    };

    const formatDate = (val) => {
      if (!val) return null;
      return new Date(val).toLocaleString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    };

    const policyDateRange = (policy) => {
      if (!policy) return null;

      const start = policy.start_date || policy.start;
      const end = policy.end_date || policy.end;

      if (!start || !end) return null;

      return `${formatDate(start)} - ${formatDate(end)}`;
    };

    const sexLabel = (val) => {
      if (val == "m") return "Male";
      if (val == "f") return "Female";
      return "-";
    };

    const memberEmail = (member) => {
      return member?.email || member?.user?.email || "-";
    };

    const memberWithPrincipalPlan = (member) => {
      return {
        ...member,
        plan: { points: [], ...(currentPrincipal.value?.plan || {}) },
      };
    };

    const scrollWorkspaceTop = () => {
      showSideCancel.value = false;
      nextTick(() => {
        if (inlineMode.value) {
          const bodyFrame = workspaceShell.value?.closest(".client-secondary-body-frame");
          const topCardLinks = bodyFrame?.previousElementSibling?.querySelector(".client-mode-strip");
          const target = topCardLinks || bodyFrame || workspaceShell.value;
          const top = target ? target.getBoundingClientRect().top + window.scrollY - 18 : 0;

          window.scrollTo({
            behavior: "smooth",
            top: Math.max(top, 0),
          });
          return;
        }

        workspaceShell.value?.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      });
    };

    const handleWorkspaceScroll = () => {
      const shell = workspaceShell.value;
      const top = workspaceTop.value;

      if (!shell || !top) {
        showSideCancel.value = false;
        return;
      }

      showSideCancel.value = shell.scrollTop > top.offsetHeight + 8;
    };

    onMounted(() => {
      if (inlineMode.value) {
        scrollWorkspaceTop();
        return;
      }

      nextTick(() => {
        handleWorkspaceScroll();
        workspaceShell.value?.addEventListener("scroll", handleWorkspaceScroll, { passive: true });
      });
    });

    onBeforeUnmount(() => {
      workspaceShell.value?.removeEventListener("scroll", handleWorkspaceScroll);
    });

    const formattedMemberSystemId = (member) => {
      const memberId = member?.id;
      if (!memberId) return "-";

      const clientPrefix =
        currentPrincipal.value?.client?.client_number ||
        currentPrincipal.value?.client?.number ||
        currentPrincipal.value?.client?.code ||
        currentPrincipal.value?.client_id ||
        null;

      const paddedMemberId = String(memberId).padStart(4, "0");

      if (!clientPrefix) {
        return paddedMemberId;
      }

      return `${clientPrefix}/${paddedMemberId}`;
    };

    const handleReset = (val) => {
      const returnPanel = activePanel.value;

      if (!val) {
        principalMode.value = "view";
        spouseMode.value = "view";
        dependantMode.value = "list";
        planMode.value = "view";
        statusMode.value = "home";
        selectedDependant.value = null;
        activePanel.value = returnPanel;
        scrollWorkspaceTop();
        return;
      }

      currentPrincipal.value = { ...currentPrincipal.value, ...val };
      idDrafts.value = {};
      principalMode.value = "view";
      spouseMode.value = "view";
      dependantMode.value = "list";
      planMode.value = "view";
      statusMode.value = "home";
      selectedDependant.value = null;
      context.emit("reset", currentPrincipal.value);
      activePanel.value = returnPanel;
      scrollWorkspaceTop();
    };

    const handleClose = () => {
      context.emit("close");
    };

    const closeInlineEdit = () => {
      principalMode.value = "view";
      spouseMode.value = "view";
      dependantMode.value = "list";
      planMode.value = "view";
      statusMode.value = "home";
      selectedDependant.value = null;
      scrollWorkspaceTop();
    };

    const openPrincipalEdit = () => {
      principalMode.value = "edit";
      scrollWorkspaceTop();
    };

    const openProviderEdit = () => {
      principalMode.value = "provider";
      spouseMode.value = "view";
      dependantMode.value = "list";
      planMode.value = "view";
      statusMode.value = "home";
      selectedDependant.value = null;
      activePanel.value = "details";
      scrollWorkspaceTop();
    };

    const openSpouseProviderEdit = () => {
      principalMode.value = "view";
      dependantMode.value = "list";
      planMode.value = "view";
      statusMode.value = "home";
      selectedDependant.value = null;
      activePanel.value = "spouse";
      spouseMode.value = "provider";
      scrollWorkspaceTop();
    };

    const openCriticalAction = (mode) => {
      statusMode.value = mode;
      scrollWorkspaceTop();
    };

    const holdCriticalAction = () => {};

    const handleEditDependant = (dependant) => {
      selectedDependant.value = dependant;
      dependantMode.value = "edit";
    };

    const handleEditDependantProvider = (dependant) => {
      principalMode.value = "view";
      spouseMode.value = "view";
      planMode.value = "view";
      statusMode.value = "home";
      selectedDependant.value = dependant;
      activePanel.value = "dependants";
      dependantMode.value = "provider";
      scrollWorkspaceTop();
    };

    return {
      accessStatusClass,
      accessStatusDetail,
      accessStatusIcon,
      accessStatusLabel,
      activePanel,
      canAddDependant,
      canAddSpouse,
      currentPrincipal,
      currentPolicy,
      currentPolicyLabel,
      dependantCapacity,
      dependantCapacityLabel,
      dependantCount,
      dependantMode,
      dependantSlotsRemaining,
      displayClientName,
      formatDate,
      formattedMemberSystemId,
      formattedSystemId,
      closeInlineEdit,
      handleClose,
      handleEditDependant,
      handleEditDependantProvider,
      handleReset,
      handleUpdateId,
      holdCriticalAction,
      canSaveId,
      idDrafts,
      idError,
      idMembers,
      inlineMode,
      isFamilyPrincipal,
      isOnHoldPrincipal,
      memberEmail,
      memberWithPrincipalPlan,
      openCriticalAction,
      openSpouseProviderEdit,
      openPrincipalEdit,
      openProviderEdit,
      planMode,
      policyId,
      principalEmail,
      principalMode,
      principalType,
      selectedDependant,
      setPanel,
      sexLabel,
      savingIdKey,
      showSideCancel,
      spouseMode,
      statusLabel,
      statusMode,
      workspaceShell,
      workspaceTop,
    };
  },
};
</script>

<style scoped>
.principal-workspace {
  bottom: 0;
  color: #263238;
  left: 0;
  height: 100vh;
  min-height: 100vh;
  overflow-y: auto;
  padding: 18px 0 45px;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 9999;
}

.principal-workspace.is-inline {
  background: #f7f7f7 !important;
  border: 1px solid #f0f0f0;
  border-radius: 0 0 6px 6px;
  border-top: 0;
  bottom: auto;
  box-shadow: 0 18px 32px -30px rgba(38, 50, 56, 0.72);
  box-sizing: border-box;
  height: auto;
  left: auto;
  min-height: 0;
  overflow: visible;
  padding: 24px;
  position: relative;
  right: auto;
  top: auto;
  width: 100%;
  z-index: auto;
}

.workspace-container {
  max-width: 1180px;
  position: relative;
  width: 92%;
}

.principal-workspace.is-inline .workspace-container {
  margin: 0;
  max-width: none;
  width: 100%;
}

.workspace-top {
  align-items: center;
  display: flex;
  justify-content: stretch;
  margin: 0 0 12px;
  min-height: 0;
}

.workspace-cancel-card {
  align-items: center;
  background: #212121;
  border: 1px solid #111;
  border-radius: 8px;
  color: #fff;
  display: flex;
  font-weight: 700;
  gap: 9px;
  justify-content: flex-start;
  min-height: 46px;
  padding: 11px 16px;
  transition: background-color 0.18s ease, transform 0.18s ease;
  width: 100%;
}

.workspace-cancel-card:hover {
  background: #b71c1c;
  color: #fff;
  transform: translateY(-1px);
}

.workspace-cancel-card i {
  font-size: 22px;
  line-height: 1;
}

.workspace-cancel-card span {
  line-height: 1;
}

.workspace-side-cancel {
  align-items: center;
  background: #212121;
  border: 1px solid #111;
  border-radius: 50%;
  color: #fff;
  display: none;
  height: 44px;
  justify-content: center;
  opacity: 0;
  pointer-events: none;
  position: fixed;
  top: 19px;
  transition: background-color 0.18s ease, opacity 0.18s ease, transform 0.18s ease;
  width: 44px;
  z-index: 10000;
}

.workspace-side-cancel:hover {
  background: #b71c1c;
  color: #fff;
  transform: translateY(-1px);
}

.workspace-side-cancel i {
  font-size: 23px;
  line-height: 1;
}

@media only screen and (min-width: 1280px) {
  .workspace-side-cancel {
    display: inline-flex;
  }

  .workspace-side-cancel.visible {
    opacity: 1;
    pointer-events: auto;
  }

  .workspace-side-cancel-left {
    left: calc((100vw - 1180px) / 4 - 22px);
  }

  .workspace-side-cancel-right {
    right: calc((100vw - 1180px) / 4 - 22px);
  }
}

.workspace-identity,
.workspace-tabs,
.workspace-panel {
  border: 1px solid #e6e0d8;
  border-radius: 8px;
  box-shadow: 0 12px 30px rgba(38, 50, 56, 0.06);
  margin-bottom: 12px;
}

.principal-workspace.is-inline .workspace-identity,
.principal-workspace.is-inline .workspace-tabs,
.principal-workspace.is-inline .workspace-panel {
  border-color: #e1e8eb;
  box-shadow: none;
  margin-bottom: 0;
}

.workspace-identity {
  padding: 22px;
}

.principal-workspace.is-inline .workspace-identity {
  border-radius: 6px;
  min-height: 122px;
  padding-right: 150px;
  position: relative;
}

.workspace-inline-close {
  align-items: center;
  background: #ffffff;
  border: 1px solid #cfd8dc;
  border-radius: 999px;
  color: #263238;
  display: inline-flex;
  font-weight: 700;
  min-height: 38px;
  padding: 0 14px;
  position: absolute;
  right: 20px;
  text-transform: none;
  top: 20px;
}

.workspace-inline-close i {
  color: #c62828;
  font-size: 19px;
}

.workspace-inline-close:hover {
  background: #f8fafb;
  border-color: #b0bec5;
}

.workspace-identity .row {
  margin-bottom: 0;
}

.workspace-identity h4 {
  color: #263238;
  font-size: 1.9rem;
  font-weight: 600;
  line-height: 1.25;
}

.principal-workspace.is-inline .workspace-identity h4 {
  font-size: 1.35rem;
  font-weight: 700;
}

.workspace-identity p {
  margin-bottom: 8px;
  margin-top: 0;
}

.workspace-identity h6 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0;
}

.workspace-identity .chip {
  border-radius: 16px;
  font-weight: 600;
}

.workspace-access-chip {
  align-items: center;
  border: 1px solid transparent;
  display: inline-flex;
  gap: 5px;
}

.workspace-access-chip i {
  font-size: 15px;
  line-height: 1;
}

.workspace-access-chip.is-active {
  background: #e8f3f2;
  border-color: #9bd2cb;
  color: #00695c;
}

.workspace-access-chip.is-pending {
  background: #fff8e1;
  border-color: #ffe082;
  color: #795548;
}

.workspace-access-chip.is-inactive {
  background: #ffebee;
  border-color: #ffcdd2;
  color: #b71c1c;
}

.workspace-hold-note {
  align-items: center;
  color: #b71c1c;
  display: flex;
  font-size: 0.82rem;
  font-weight: 650;
  gap: 7px;
  line-height: 1.35;
  margin: 2px 0 0;
}

.workspace-hold-note i {
  font-size: 18px;
}

.workspace-tabs {
  align-items: center;
  display: flex;
  gap: 6px;
  overflow-x: auto;
  padding: 9px;
}

.principal-workspace.is-inline .workspace-tabs {
  background: #f7fafb !important;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: none;
  margin: 18px 0 20px;
  padding: 10px;
}

.workspace-tab {
  align-items: center;
  border: 1px solid transparent;
  border-radius: 6px;
  color: #546e7a;
  display: inline-flex;
  flex: 0 0 auto;
  gap: 6px;
  font-weight: 600;
  min-height: 42px;
  padding: 10px 13px;
  transition: background-color 0.18s ease, border-color 0.18s ease, color 0.18s ease;
}

.principal-workspace.is-inline .workspace-tab {
  background: transparent;
  border-color: #b7c9d0;
  color: #455a64;
  font-size: 0.82rem;
  font-weight: 750;
  min-height: 38px;
  padding: 10px 13px;
  text-transform: uppercase;
}

.workspace-tab i {
  font-size: 19px;
}

.principal-workspace.is-inline .workspace-tab i {
  font-size: 18px;
}

.workspace-tab:hover,
.workspace-tab.active {
  background: #e8f5f3;
  border-color: #b2dfdb;
  color: #00695c;
}

.principal-workspace.is-inline .workspace-tab:hover,
.principal-workspace.is-inline .workspace-tab.active {
  background: #ffffff;
  border-color: #80cbc4;
  box-shadow: inset 0 -2px 0 #009688;
  color: #00695c;
}

.workspace-tab.danger.active,
.workspace-tab.danger:hover {
  background: #ffebee;
  border-color: #ffcdd2;
  color: #c62828;
}

.principal-workspace.is-inline .workspace-tab.danger.active,
.principal-workspace.is-inline .workspace-tab.danger:hover {
  background: #ffffff;
  border-color: #ffcdd2;
  box-shadow: inset 0 -2px 0 #ef9a9a;
  color: #b71c1c;
}

.workspace-tab.disabled {
  color: #bdbdbd;
  cursor: default;
  pointer-events: none;
}

.workspace-tab span {
  white-space: nowrap;
}

.workspace-panel {
  min-height: 560px;
  padding: 22px;
}

.principal-workspace.is-inline .workspace-panel {
  background: #ffffff !important;
  border-radius: 6px;
  box-shadow: none;
  min-height: 460px;
  padding: 24px;
}

.workspace-panel :deep(.card),
.workspace-panel :deep(.card-panel) {
  border: 1px solid #eeeeee;
  border-radius: 8px;
  box-shadow: none !important;
}

.workspace-panel :deep(.card-panel) {
  background-color: #fff;
  margin: 0 0 12px;
  padding: 18px;
}

.workspace-panel :deep(.card-content) {
  padding: 18px;
}

.workspace-panel :deep(.input-field) {
  margin-bottom: 0;
}

.workspace-panel :deep(.btn),
.workspace-panel :deep(.btn-large),
.workspace-panel :deep(button) {
  border-radius: 6px;
  box-shadow: none;
}

.workspace-panel :deep(.btn-flat) {
  color: #455a64;
  font-weight: 600;
}

.workspace-panel :deep(img.circle) {
  border: 3px solid #fff;
  box-shadow: 0 0 0 1px #e0e0e0;
}

.detail-section {
  background: #fff;
  border: 1px solid #dedede;
  border-radius: 8px;
  overflow: hidden;
}

.detail-heading {
  align-items: center;
  background: #f8faf9;
  border-bottom: 1px solid #dedede;
  display: flex;
  justify-content: space-between;
  padding: 15px 18px;
}

.detail-heading span {
  align-items: center;
  display: inline-flex;
  gap: 12px;
}

.detail-heading h6 {
  color: #263238;
  font-weight: 600;
  margin: 0;
}

.detail-heading small {
  color: #757575;
}

.principal-action-bar {
  flex-wrap: wrap;
  justify-content: flex-end;
}

.edit-top-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
}

.inline-back-icon {
  align-items: center;
  border: 1px solid #e0e0e0;
  border-radius: 50%;
  color: #455a64;
  display: inline-flex;
  height: 34px;
  justify-content: center;
  width: 34px;
}

.inline-back-icon:hover {
  background: #f5f5f5;
  color: #263238;
}

.inline-back-icon i {
  font-size: 20px;
  line-height: 1;
}

.edit-action {
  border-radius: 6px;
  box-shadow: none;
  font-weight: 600;
}

.account-action {
  background: #455a64;
}

.account-action:hover {
  background: #37474f;
}

.provider-action {
  background: #123c69;
}

.provider-action:hover {
  background: #0d2f54;
}

.dependant-detail-section {
  margin-bottom: 14px;
}

.detail-row {
  align-items: flex-start;
  border-bottom: 1px solid #eeeeee;
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(130px, 220px) 1fr;
  min-height: 50px;
  padding: 14px 18px;
}

.detail-row:last-child {
  border-bottom: 0;
}

.detail-row span {
  color: #607d8b;
  font-size: 0.85rem;
  font-weight: 600;
}

.detail-row strong {
  color: #263238;
  font-weight: 500;
  overflow-wrap: anywhere;
}

.access-highlight-row {
  background: #fbfcfc;
}

.access-highlight-row strong {
  align-items: center;
  display: inline-flex;
  font-weight: 700;
  gap: 7px;
}

.access-highlight-row strong i {
  color: #00897b;
  font-size: 19px;
}

.access-highlight-row.is-inactive {
  background: #fff8f8;
}

.access-highlight-row.is-inactive strong {
  color: #b71c1c;
}

.access-highlight-row.is-inactive strong i {
  color: #c62828;
}

.principal-type-value {
  align-items: center;
  display: inline-flex;
  gap: 7px;
}

.principal-type-value i {
  color: #123c69;
  font-size: 20px;
}

.plan-highlight-row strong {
  color: #123c69;
  font-weight: 700;
}

.critical-workbench {
  background: #fff;
  border: 1px solid #dedede;
  border-radius: 8px;
  display: grid;
  grid-template-columns: 260px 1fr;
  min-height: 460px;
  overflow: hidden;
}

.critical-menu {
  background: #f8faf9;
  border-right: 1px solid #dedede;
  display: grid;
  grid-auto-rows: min-content;
}

.critical-menu-item {
  align-items: center;
  border-bottom: 1px solid #eeeeee;
  color: #263238;
  display: grid;
  gap: 12px;
  grid-template-columns: 32px 1fr auto;
  min-height: 72px;
  padding: 14px;
  transition: background-color 0.18s ease, color 0.18s ease;
}

.critical-menu-item:hover {
  background: #fff;
}

.critical-menu-item i {
  align-items: center;
  background: #eceff1;
  border-radius: 50%;
  color: #123c69;
  display: inline-flex;
  font-size: 19px;
  height: 32px;
  justify-content: center;
  width: 32px;
}

.critical-menu-item strong,
.critical-menu-item small {
  display: block;
}

.critical-menu-item strong {
  color: #263238;
  font-weight: 600;
}

.critical-menu-item small {
  color: #607d8b;
  font-size: 0.82rem;
  margin-top: 2px;
}

.critical-menu-item em {
  color: #78909c;
  font-size: 0.78rem;
  font-style: normal;
  font-weight: 600;
}

.critical-menu-item.active {
  background: #fff;
  border-left: 4px solid #123c69;
  padding-left: 10px;
}

.critical-menu-item.active i {
  background: #123c69;
  color: #fff;
}

.critical-menu-item.disabled {
  cursor: default;
  opacity: 0.72;
}

.critical-menu-item.disabled:hover {
  background: transparent;
}

.critical-menu-item.danger i {
  background: #ffebee;
  color: #c62828;
}

.critical-menu-item.danger.active {
  border-left-color: #c62828;
}

.critical-menu-item.danger.active i {
  background: #c62828;
  color: #fff;
}

.critical-main {
  background: #fff;
  padding: 20px;
}

.critical-home {
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 22px;
}

.critical-home-heading {
  align-items: center;
  border-bottom: 1px solid #eeeeee;
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 14px;
}

.critical-home-heading > i {
  align-items: center;
  background: #fff8e1;
  border-radius: 50%;
  color: #f57f17;
  display: inline-flex;
  height: 42px;
  justify-content: center;
  width: 42px;
}

.critical-home-heading h6 {
  font-weight: 600;
  margin: 0;
}

.critical-home-heading small {
  color: #757575;
}

.critical-home p {
  color: #455a64;
  line-height: 1.65;
  margin: 0 0 18px;
  max-width: 760px;
}

.critical-summary {
  border: 1px solid #eeeeee;
  border-radius: 8px;
  display: grid;
  overflow: hidden;
}

.critical-summary div {
  align-items: flex-start;
  border-bottom: 1px solid #eeeeee;
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(120px, 190px) 1fr;
  padding: 13px 16px;
}

.critical-summary div:last-child {
  border-bottom: 0;
}

.critical-summary span {
  color: #607d8b;
  font-size: 0.85rem;
  font-weight: 600;
}

.critical-summary strong {
  color: #263238;
  font-weight: 500;
  overflow-wrap: anywhere;
}

.id-update-panel {
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 22px;
}

.id-update-note {
  color: #455a64;
  line-height: 1.6;
  margin: 0 0 18px;
}

.id-update-list {
  display: grid;
  gap: 12px;
}

.id-update-row {
  align-items: center;
  border: 1px solid #eeeeee;
  border-radius: 8px;
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(180px, 1fr) minmax(260px, 360px);
  padding: 14px;
}

.id-update-row strong,
.id-update-row small {
  display: block;
}

.id-update-row small {
  color: #607d8b;
  margin-top: 4px;
}

.id-update-controls {
  align-items: center;
  display: grid;
  gap: 8px;
  grid-template-columns: 1fr auto;
}

.id-update-controls input {
  border: 1px solid #dfe5e7 !important;
  border-radius: 6px !important;
  box-sizing: border-box !important;
  height: 38px !important;
  margin: 0 !important;
  padding: 0 10px !important;
}

.dependant-actions {
  margin-bottom: 14px;
}

.empty-utilization {
  align-items: center;
  color: #546e7a;
  display: inline-flex;
  gap: 8px;
  justify-content: center;
}

.empty-utilization i {
  color: #00897b;
}

.m-0 {
  margin: 0;
}

@media only screen and (min-width: 601px) and (max-width: 992px) {
  .principal-workspace.is-inline {
    padding: 18px;
  }

  .principal-workspace.is-inline .workspace-panel {
    padding: 18px;
  }

  .detail-heading {
    align-items: flex-start;
    gap: 14px;
  }

  .detail-row {
    gap: 14px;
    grid-template-columns: minmax(120px, 160px) minmax(0, 1fr);
  }
}

@media only screen and (max-width: 600px) {
  .principal-workspace.is-inline {
    padding: 14px;
  }

  .workspace-container {
    width: 100%;
  }

  .principal-workspace.is-inline .workspace-identity {
    padding-right: 16px;
  }

  .workspace-inline-close {
    margin-bottom: 14px;
    position: static;
  }

  .workspace-identity {
    padding: 16px;
  }

  .workspace-panel {
    padding: 14px;
  }

  .principal-workspace.is-inline .workspace-panel {
    padding: 14px;
  }

  .detail-heading {
    align-items: stretch;
    flex-direction: column;
    gap: 14px;
  }

  .detail-heading span,
  .principal-action-bar {
    align-items: stretch;
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }

  .principal-action-bar .edit-action {
    width: 100%;
  }

  .detail-heading small {
    text-align: right;
  }

  .detail-row {
    gap: 5px;
    grid-template-columns: minmax(0, 1fr);
    min-height: 0;
    padding: 15px 18px;
  }

  .detail-row span,
  .detail-row strong {
    min-width: 0;
  }

  .detail-row strong {
    font-size: 1.02rem;
    line-height: 1.45;
    width: 100%;
  }

  .principal-type-value,
  .access-highlight-row strong {
    justify-content: flex-start;
  }

  .critical-workbench {
    grid-template-columns: 1fr;
  }

  .critical-menu {
    border-bottom: 1px solid #e0e0e0;
    border-right: 0;
  }

  .critical-summary div {
    grid-template-columns: 1fr;
    gap: 4px;
  }

  .id-update-row,
  .id-update-controls {
    grid-template-columns: 1fr;
  }
}
</style>
